import { employerVerificationDashboardHttp } from './employerVerificationDashboardClient';

const BASE = '/api/employer-verification/v1/admin/analytics';

export type AnalyticsDateRangeDto = { verifiedFrom: string; verifiedTo: string };

export type AnalyticsSummaryResponse = {
  dateRange: AnalyticsDateRangeDto;
  outcomes: Array<{ finalStatusBucket: string; count: number }>;
  gateFailures: {
    existenceFail: number;
    linkageFail: number;
    legitimacyFail: number;
    structuralFail: number;
    temporalFail: number;
  };
  meta: { googlePlaceHint: string };
};

export type MultiAttemptAnalyticsResponse = {
  dateRange: AnalyticsDateRangeDto;
  total: number;
  limit: number;
  offset: number;
  items: Array<{
    applicationId: string;
    verificationRowCount: number;
    firstVerifiedAt: string | null;
    lastVerifiedAt: string | null;
    verificationIds: string[];
    fraudSignals: Array<{
      id: string;
      signalName: string;
      riskContributionText: string;
      observedAt: string | null;
      verificationId: string | null;
      observationPreview: string | null;
    }>;
  }>;
};

export type EmployerRollupDto = {
  entityId: string;
  legalName: string | null;
  ceid: string | null;
  corporationId: string | null;
  businessNumber: string | null;
  registrySource: string | null;
  website: string | null;
  sector: string | null;
  registryStatus: string | null;
  registryVerifiedAt: string | null;
  isActive: boolean | null;
  totalVerifications: number;
  verifiedCount: number;
  rejectedCount: number;
  manualReviewCount: number;
  unknownCount: number;
  lastVerifiedAt: string | null;
  placeId: string | null;
  placeNameIntel: string | null;
  businessStatusIntel: string | null;
  ratingIntel: string | null;
  userRatingsTotalIntel: string | null;
  domainAgeDaysIntel: string | null;
  buildingTypeIntel: string | null;
  normalizedAddressPrimary: string | null;
  cityPrimary: string | null;
  provincePrimary: string | null;
};

export type EmployerRollupsAnalyticsResponse = {
  dateRange: AnalyticsDateRangeDto;
  total: number;
  limit: number;
  offset: number;
  items: EmployerRollupDto[];
};

export type EmployerLocationRowDto = {
  locId: string;
  normalizedAddress: string | null;
  branchType: string | null;
  lat: number | null;
  lng: number | null;
  city: string | null;
  province: string | null;
};

export type EmployerDetailAnalyticsResponse = {
  dateRange: AnalyticsDateRangeDto;
  rollup: EmployerRollupDto;
  rawPlaceResponse: Record<string, unknown> | null;
  locations: EmployerLocationRowDto[];
};

export type EmployerAnalyticsQuery = {
  verifiedFrom?: string;
  verifiedTo?: string;
};

function paramsFromRange(q?: EmployerAnalyticsQuery): Record<string, string> | undefined {
  if (!q) return undefined;
  const o: Record<string, string> = {};
  if (q.verifiedFrom) o.verifiedFrom = q.verifiedFrom;
  if (q.verifiedTo) o.verifiedTo = q.verifiedTo;
  return Object.keys(o).length ? o : undefined;
}

export const employerVerificationAnalyticsApi = {
  summary(range?: EmployerAnalyticsQuery) {
    return employerVerificationDashboardHttp
      .get<AnalyticsSummaryResponse>(`${BASE}/summary`, {
        params: paramsFromRange(range),
      })
      .then((r) => r.data);
  },

  multiAttemptApplications(
    range: EmployerAnalyticsQuery | undefined,
    limit: number,
    offset: number,
  ) {
    return employerVerificationDashboardHttp
      .get<MultiAttemptAnalyticsResponse>(`${BASE}/applications/multi-attempt`, {
        params: { ...paramsFromRange(range), limit, offset },
      })
      .then((r) => r.data);
  },

  employerRollups(
    range: EmployerAnalyticsQuery | undefined,
    opts: { limit: number; offset: number; employerNameContains?: string },
  ) {
    return employerVerificationDashboardHttp
      .get<EmployerRollupsAnalyticsResponse>(`${BASE}/employers`, {
        params: {
          ...paramsFromRange(range),
          limit: opts.limit,
          offset: opts.offset,
          ...(opts.employerNameContains?.trim()
            ? { employerNameContains: opts.employerNameContains.trim() }
            : {}),
        },
      })
      .then((r) => r.data);
  },

  employerDetail(entityId: string, range?: EmployerAnalyticsQuery) {
    return employerVerificationDashboardHttp
      .get<EmployerDetailAnalyticsResponse>(
        `${BASE}/employers/${encodeURIComponent(entityId)}`,
        {
          params: paramsFromRange(range),
        },
      )
      .then((r) => r.data);
  },
};
